import type React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  FormControl,
  MenuItem,
  InputAdornment,
  useTheme,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import CustomTextField from "../../../../components/ui/Textfield";
import CustomSelect from "../../../../components/ui/Select";
import { tokens } from "../../../../theme";
import type {
  IAddServiceModalProps,
  IService,
  ICompany,
} from "../../../../types/package";
import apiService from "../../../../service/Api";
import { toast } from "react-toastify";
import DescriptionRichTextEditor from "../../components/DescriptionRichTextEditor";

const AddServiceModal: React.FC<IAddServiceModalProps> = ({
  open,
  onClose,
  onServiceAdded,
  selectedService,
  isEditMode = false,
}) => {
  const [serviceName, setServiceName] = useState("");
  const [description, setDescription] = useState("");

  const [price, setPrice] = useState("");
  const [type, setType] = useState("One-time");
  const [variation, setVariation] = useState("");
  const [status, setStatus] = useState("Active");
  const [category, setCategory] = useState("Screen");
  const [isActive, setIsActive] = useState(true);
  const [packageCategory, setPackageCategory] = useState("funeral");
  const [funeralHomes, setFuneralHomes] = useState<ICompany[]>([]);
  const [funeralHomesLoading, setFuneralHomesLoading] = useState(false);
  const [selectedFuneralHomes, setSelectedFuneralHomes] = useState<ICompany[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  useEffect(() => {
    if (open) {
      if (selectedService && isEditMode) {
        setServiceName(selectedService.name || "");
        setServiceName(selectedService.name || "");
        setDescription(selectedService.description || "");
        setPrice(selectedService.price?.toString() || "");
        setType(selectedService.type || "One-time");
        setVariation(selectedService.variation || "");
        setStatus(selectedService.status || "Active");
        setCategory(selectedService.service_category || "Video & Audio");
        setIsActive(selectedService.isActive ?? true);
        setPackageCategory(selectedService.category || "funeral");
        setSelectedFuneralHomes(
          (selectedService.funeral_homes || []).map((fh) => ({
            id: fh.id,
            name: fh.name,
            company_type: "funeral_home",
          }))
        );
      } else {
        setServiceName("");
        setDescription("");
        setPrice("");
        setType("One-time");
        setVariation("");
        setStatus("Active");
        setCategory("Video & Audio");
        setIsActive(true);
        setPackageCategory("funeral");
        setSelectedFuneralHomes([]);
      }
      setError(null);
    }
  }, [open, selectedService, isEditMode]);

  useEffect(() => {
    if (!open || packageCategory !== "funeral_home") {
      return;
    }
    let cancelled = false;
    setFuneralHomesLoading(true);
    apiService
      .get<{ results: ICompany[] }>(
        "api/companies/company/?company_type=funeral_home&page_size=200"
      )
      .then((res) => {
        if (!cancelled) {
          setFuneralHomes(res.data.results || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("Failed to load funeral homes.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setFuneralHomesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, packageCategory]);

  const validateForm = () => {
    if (!serviceName.trim()) {
      setError("Service name is required");
      return false;
    }
    if (!price || Number.parseFloat(price) < 0) {
      setError("Valid price is required");
      return false;
    }
    if (!category) {
      setError("Category is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const serviceData = {
        name: serviceName.trim(),
        description: description.trim() || null,
        price: Number.parseFloat(price),
        type,
        variation: variation.trim() || null,
        status,
        service_category: category,
        category: packageCategory,
        funeral_home_ids:
          packageCategory === "funeral_home"
            ? selectedFuneralHomes.map((fh) => fh.id)
            : [],
        isActive,
      };

      if (selectedService?.id && isEditMode) {
        await apiService.put<IService>(
          `api/packages/package-service/${selectedService.id}/`,
          serviceData
        );
        toast.success("Service updated successfully!");
      } else {
        await apiService.post<IService>(
          "api/packages/package-service/",
          serviceData
        );
        toast.success("Service created successfully!");
      }

      if (onServiceAdded) {
        onServiceAdded();
      }
      onClose();
    } catch (err: unknown) {
      const errorResponse = (err as {
        response?: { data?: { message?: string; detail?: string; error?: string } };
      })?.response;
      const errorMessage =
        errorResponse?.data?.message ||
        errorResponse?.data?.detail ||
        errorResponse?.data?.error ||
        `Failed to ${
          isEditMode ? "update" : "create"
        } service. Please try again.`;

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          PaperProps={{
            component: motion.div,
            initial: { opacity: 0, scale: 0.9, y: 20 },
            animate: { opacity: 1, scale: 1, y: 0 },
            exit: { opacity: 0, scale: 0.9, y: 20 },
            transition: { duration: 0.3, ease: "easeOut" },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isEditMode ? "Edit Service" : "Add New Service"}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3, mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Service Name */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <CustomTextField
                  fullWidth
                  label="Service Name"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  variant="outlined"
                  required
                />
              </motion.div>

              {/* Price */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <CustomTextField
                  fullWidth
                  label="Price"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  variant="outlined"
                  type="number"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </motion.div>

              {/* Category */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <FormControl fullWidth>
                  <CustomSelect
                    value={category}
                    onChange={(e) => setCategory(e.target.value as string)}
                    label="Category"
                    required
                  >
                    <MenuItem value="Screens">Screens</MenuItem>
                    <MenuItem value="Video & Audio">Video & Audio</MenuItem>
                    <MenuItem value="AV Equipment">AV Equipment</MenuItem>
                    <MenuItem value="Design">Design</MenuItem>
                    <MenuItem value="Old Mutual">Old Mutual</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </CustomSelect>
                </FormControl>
              </motion.div>

              {/* Package Category */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.35 }}
              >
                <FormControl fullWidth>
                  <CustomSelect
                    value={packageCategory}
                    onChange={(e) =>
                      setPackageCategory(e.target.value as string)
                    }
                    label="Package Category"
                    required
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="funeral">Funeral</MenuItem>
                    <MenuItem value="funeral_home">Funeral Home</MenuItem>
                    <MenuItem value="conference">Conference</MenuItem>
                  </CustomSelect>
                </FormControl>
              </motion.div>

              {packageCategory === "funeral_home" && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.37 }}
                >
                  <Autocomplete
                    multiple
                    options={funeralHomes}
                    value={selectedFuneralHomes}
                    onChange={(_e, newValue) =>
                      setSelectedFuneralHomes(newValue)
                    }
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    loading={funeralHomesLoading}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        label="Funeral Homes"
                        placeholder="Leave empty to apply to all funeral homes"
                        variant="outlined"
                        helperText="Restrict this service to specific funeral homes, or leave empty for all"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {funeralHomesLoading && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => (
                        <Chip
                          label={option.name}
                          {...getTagProps({ index })}
                          key={option.id}
                        />
                      ))
                    }
                  />
                </motion.div>
              )}

              {/* Description */}

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Typography variant="subtitle1">Package Description</Typography>
                <DescriptionRichTextEditor
                  value={description}
                  onChange={(html) => setDescription(html)}
                  placeholder="Enter package description..."
                />
              </motion.div>

              {/* Active Status */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#8b5a3c",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { backgroundColor: "#8b5a3c" },
                      }}
                    />
                  }
                  label="Active Service"
                />
              </motion.div>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, borderTop: "1px solid rgba(0,0,0,0.08)" }}>
            <Button
              onClick={onClose}
              sx={{ color: colors.primary[300] }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              sx={{
                backgroundColor: colors.primary[400],
                "&:hover": { backgroundColor: colors.primary[300] },
              }}
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                ? "Update Service"
                : "Add Service"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default AddServiceModal;
